import { collection, query, where, getDocs } from 'firebase/firestore';
import { firestore } from './firebase';

export interface ParkingBooking {
  userId: string;
  status: string;
  bookingStart: number; // Unix timestamp in milliseconds
  // Add other fields as needed
}

export interface ParkingBookingsAnalytics {
  totalBookings: number;
  uniqueUsers: number;
  bookingsByStatus: Record<string, {
    count: number;
    uniqueUsers: number;
  }>;
}

/**
 * Fetch parking bookings for a specific billing period (month)
 * @param billingPeriod - Billing period in YYYY-MM format (e.g., "2025-11")
 */
export const fetchParkingBookings = async (
  billingPeriod: string
): Promise<ParkingBooking[]> => {
  try {
    console.log(`Fetching parking bookings for billing period: ${billingPeriod}`);

    // Parse the billing period to get start and end timestamps
    const [year, month] = billingPeriod.split('-').map(Number);
    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 1); // First day of next month

    // Convert to Unix timestamps in milliseconds
    const startTimestamp = startDate.getTime();
    const endTimestamp = endDate.getTime();

    console.log('Date range:', {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      startTimestamp,
      endTimestamp
    });

    const parkingBookingsRef = collection(firestore, 'parking_bookings');

    const q = query(
      parkingBookingsRef,
      where('bookingStart', '>=', startTimestamp),
      where('bookingStart', '<', endTimestamp)
    );

    console.log('Executing Firestore query for parking_bookings...');
    const querySnapshot = await getDocs(q);
    console.log(`Query returned ${querySnapshot.size} documents`);

    const bookings: ParkingBooking[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      console.log('Booking document:', doc.id, data);
      bookings.push({
        userId: data.userId,
        status: data.status || 'unknown',
        bookingStart: data.bookingStart,
      });
    });

    console.log(`Successfully fetched ${bookings.length} parking bookings`);
    return bookings;
  } catch (error) {
    console.error('Error fetching parking bookings:', error);
    if (error instanceof Error) {
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
    }
    throw error;
  }
};

/**
 * Analyze parking bookings to compute conversion metrics
 */
export const analyzeParkingBookings = (bookings: ParkingBooking[]): ParkingBookingsAnalytics => {
  // Count total bookings
  const totalBookings = bookings.length;

  // Get unique users
  const uniqueUserIds = new Set<string>();
  bookings.forEach(booking => {
    uniqueUserIds.add(booking.userId);
  });

  // Group by status
  const bookingsByStatus: Record<string, {
    count: number;
    uniqueUsers: number;
  }> = {};

  // First, count bookings by status
  bookings.forEach(booking => {
    const status = booking.status;
    if (!bookingsByStatus[status]) {
      bookingsByStatus[status] = {
        count: 0,
        uniqueUsers: 0,
      };
    }
    bookingsByStatus[status].count++;
  });

  // Then, count unique users per status
  Object.keys(bookingsByStatus).forEach(status => {
    const usersForStatus = new Set<string>();
    bookings
      .filter(b => b.status === status)
      .forEach(b => usersForStatus.add(b.userId));
    bookingsByStatus[status].uniqueUsers = usersForStatus.size;
  });

  return {
    totalBookings,
    uniqueUsers: uniqueUserIds.size,
    bookingsByStatus,
  };
};
